import {useState} from 'react';
import {
    Alert,
    Button,
    Group,
    Modal,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import {AiOutlineInfoCircle, AiOutlineWarning} from 'react-icons/ai';

import * as PlaybooksService from '../../playbooks/services/PlaybooksService';
import {useQuery} from '@tanstack/react-query';
import {Playbook, PlaybookStatus} from '../../playbooks/types';
import {Application} from '../types';

type ProvisionApplicationModalProps = {
    opened: boolean,
    onClose: () => void,
    onConfirm: (
        playbookId: string,
        stage: string,
        subdomain: string,
    ) => void,
    provisioning?: boolean,
    applicationName?: string,
    record?: Application,
};

// FNP-1013. The admin picks the playbook that will provision this application.
//
// ⚠️ THE WHOLE POINT IS THAT THE ID COMES FROM HERE. It used to be a
// placeholder substituted at `cdk synth` from per-stage config, because an
// APPSYNC_JS function has no environment variables — so a playbook an admin
// created in a console needed a commit and a pipeline deploy in every
// environment before it could be used. Roughly six deploys to wire one
// playbook. A dropdown removes that entirely.
//
// ⚠️ NOT a name-convention lookup. Resolving the playbook by name, or by an
// `appliesTo` discriminator, is the same brittleness with an extra step — the
// owner rejected it explicitly. The admin chooses from what exists in THIS
// environment.
//
// ⚠️ `stage` is a deliberate choice, not a default. The mutation requires it
// for the same reason deprovision does: a defaulted stage on a consequential
// operation is how the wrong environment gets touched.

const STAGE_OPTIONS = [
    {value: 'dev', label: 'dev'},
    {value: 'test', label: 'test'},
    {value: 'prod', label: 'prod'},
];

export const ProvisionApplicationModal = ({
                                              opened,
                                              onClose,
                                              onConfirm,
                                              provisioning = false,
                                              applicationName,
                                              record,
                                          }: ProvisionApplicationModalProps) => {

    const [playbookId, setPlaybookId] = useState<string | null>(null);
    const [stage, setStage] = useState<string | null>(null);

    // FNP-1499: `businessDomain` and `applicationType` USED TO BE ASKED HERE.
    //
    // FNP-1013 added them because the playbook's inputSchema REQUIRED them and
    // a record created without them could never be provisioned. That was the
    // right fix for the contract as it stood; the contract was wrong. Nothing
    // downstream reads either field, so the dialog was making the operator
    // re-answer two dead questions the create form had already asked.
    //
    // The playbook stopped requiring them first, so nothing here can be refused
    // for their absence.

    // FNP-1231: the leftmost DNS label — and the difference between this
    // application getting its own host and it trying to take the zone apex.
    //
    // ⚠️ AN EMPTY SUBDOMAIN IS NOT A NEUTRAL DEFAULT. The orchestrator's
    // composeAppDomain deliberately falls back to the hosted-zone apex, which
    // is a genuine FNP-287 feature — flexinet-marketing-app deploys at the
    // apex on purpose. But an apex is almost always already owned, so an
    // UNINTENDED fallback asks CloudFront for a CNAME another distribution
    // holds, is refused with a 409, and rolls the stack back — after both
    // repos and both pipelines have already been created.
    //
    // `fnp1013-provision-test` did exactly that on 2026-08-25: no subdomain,
    // so it tried to claim the live marketing site's `test.flexi.network`.
    //
    // Seeded from `name`, not left blank, because the app's own name is the
    // host an admin almost always wants and typing it again is friction with
    // no upside. Apex remains reachable by clearing the field deliberately.
    const [subdomain, setSubdomain] = useState<string | null>(null);
    const seededSubdomain = subdomain ?? record?.subdomain ?? record?.name ?? '';


    const {
        data,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['playbooks', 'provision-options'],
        queryFn: () => PlaybooksService.fetchRecords({}),
        enabled: opened,
        staleTime: 5 * 60 * 1000,
    });

    // FNP-565: fetchRecords returns a PAGE envelope ({items, nextToken}), not a
    // bare array, now that the playbooks list is token-paginated.
    //
    // ⚠️ This dropdown reads the FIRST page only, which is what it did before
    // the change too — fetchRecords was never paginated here. An operator whose
    // runnable playbooks all sit past page one would see an empty list. Left as
    // is deliberately: making this modal paginate is a behaviour change outside
    // this ticket, and the `skipped` note below already tells the admin the list
    // is filtered.
    const playbooks: Playbook[] = data?.items ?? [];

    // Only an ACTIVE playbook with a stateMachineArn can actually run.
    // workflows-api enforces both — this filter is a convenience so the admin
    // is not offered a choice that will be refused, NOT a second gate. Do not
    // add validation here; that is the cross-boundary check the platform
    // rules forbid.
    const runnable = playbooks.filter(playbook =>
        playbook.status === PlaybookStatus.ACTIVE && !!playbook.stateMachineArn,
    );

    const options = runnable.map(playbook => ({
        value: playbook.id,
        label: playbook.displayName?.trim() || playbook.name?.trim() || playbook.id,
    }));

    const skipped = playbooks.length - runnable.length;

    // Both are required before this can run. `stage` has no default for the
    // same reason deprovision's does not: a defaulted stage on a consequential
    // operation is how the wrong environment gets touched.
    const ready = Boolean(playbookId && stage) && !provisioning;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={'Provision infrastructure'}
            centered
        >
            <Stack gap={'md'}>

                <Text size={'sm'}>
                    {applicationName
                        ? `Build the infrastructure for ${applicationName}.`
                        : 'Build this application\'s infrastructure.'}
                    {' '}This creates repositories, pipelines, a bucket, a
                    distribution and DNS. It takes several minutes.
                </Text>

                {isError && (
                    <Alert variant={'light'} color={'red'} icon={<AiOutlineInfoCircle/>}>
                        The playbooks could not be read, so there is nothing to
                        choose from.
                    </Alert>
                )}

                <Select
                    label={'Playbook'}
                    description={'The workflow that will build it. Only ACTIVE playbooks bound to a state machine can run.'}
                    placeholder={isLoading ? 'Loading…' : 'Select a playbook'}
                    data={options}
                    value={playbookId}
                    onChange={setPlaybookId}
                    disabled={isLoading || provisioning}
                    withAsterisk
                    searchable
                />

                {skipped > 0 && (
                    <Text size={'xs'} c={'dimmed'}>
                        {skipped} playbook{skipped === 1 ? ' is' : 's are'} not
                        listed — a playbook must be ACTIVE and bound to a state
                        machine before it can run.
                    </Text>
                )}

                <Select
                    label={'Stage'}
                    description={'Which environment to provision into. Chosen deliberately — there is no default.'}
                    placeholder={'Select a stage'}
                    data={STAGE_OPTIONS}
                    value={stage}
                    onChange={setStage}
                    disabled={provisioning}
                    withAsterisk
                />

                {/* FNP-1231: the label that decides host vs. apex. */}
                <TextInput
                    label={'Subdomain'}
                    description={'The leftmost DNS label for this application, e.g. "reports" for reports.test.flexi.network.'}
                    placeholder={'Leave empty to deploy at the domain apex'}
                    value={seededSubdomain}
                    onChange={(e) => setSubdomain(e.currentTarget.value)}
                    disabled={provisioning}
                    data-testid={'provision-subdomain'}
                />

                {/*
                  * Empty is legal — apex deployment is real (FNP-287) — but it
                  * is nearly always a mistake, and the mistake is expensive:
                  * the CNAME clash surfaces at CloudFormation, minutes later,
                  * after the repos and pipelines exist. So it is allowed and
                  * called out, rather than blocked.
                  */}
                {seededSubdomain.trim() === '' && (
                    <Alert
                        variant={'light'}
                        color={'orange'}
                        icon={<AiOutlineWarning/>}
                        title={'This will deploy at the domain apex'}
                    >
                        <Text size={'sm'}>
                            With no subdomain this application claims the parent domain
                            itself. If another application already serves that domain,
                            provisioning fails partway — after the repositories and
                            pipelines have been created — and the stack rolls back.
                        </Text>
                    </Alert>
                )}

                <Group justify={'flex-end'} mt={'md'}>
                    <Button variant={'subtle'} onClick={onClose} disabled={provisioning}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (playbookId && stage) {
                                onConfirm(
                                    playbookId,
                                    stage,
                                    seededSubdomain.trim(),
                                );
                            }
                        }}
                        disabled={!ready}
                        loading={provisioning}
                    >
                        Provision
                    </Button>
                </Group>

            </Stack>
        </Modal>
    );
};
